/**
 * Persona/offer generate dialog for AI landing pages (#6480, FE-6).
 *
 * Two ways to seed a page: pick a saved persona (from_persona → icp_id) or describe a custom
 * offer (from_offer → theme). A persona takes priority — while one is selected the offer is
 * disabled, and clearing it re-enables the offer. The POST returns a GENERATING page, so on
 * success the dialog records the public_id, invalidates the business list so that row appears,
 * and closes; the list owns the async Celery progress from there.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { Sparkles } from 'lucide-react';

import { FieldError } from '@/components/glitch-ui/field-error';
import { Spinner } from '@/components/glitch-ui/spinner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import { createBusinessLandingPage } from '@/lib/apis';
import type { Icp } from '@/lib/types';
import { landingPagesByBusinessAtom } from '@/store';

interface GenerateLandingPageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: number;
  organizationId: number;
  icps: Icp[];
}

export function GenerateLandingPageDialog({
  open,
  onOpenChange,
  businessId,
  organizationId,
  icps,
}: GenerateLandingPageDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const trackPage = useSetAtom(landingPagesByBusinessAtom);

  const [icpId, setIcpId] = useState<string | undefined>(undefined);
  const [offer, setOffer] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  // Reset the form each time the dialog opens. The parent owns `open`, so Radix's onOpenChange
  // does not fire on a programmatic open — an effect is the only place a fresh open is observable.
  useEffect(() => {
    if (open) {
      setIcpId(undefined);
      setOffer('');
      setError(undefined);
    }
  }, [open]);

  const generate = useMutation({
    mutationFn: (opts: { icpId?: string; theme?: string }) =>
      createBusinessLandingPage(businessId, organizationId, opts),
    onSuccess: (page) => {
      trackPage((prev) => ({ ...prev, [businessId]: [page.public_id, ...(prev[businessId] ?? [])] }));
      queryClient.invalidateQueries({ queryKey: ['landing-pages-by-business', businessId] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast({
        title: 'Failed to generate landing page',
        description: err instanceof Error ? err.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (!next && generate.isPending) return; // don't abandon an in-flight generation by accident
    onOpenChange(next);
  };

  const handleGenerate = () => {
    // Tell-on-click (#5857): the button stays enabled; a click with nothing chosen marks the
    // fields instead of sitting silently disabled. A persona wins when both are present.
    if (!icpId && !offer.trim()) {
      setError('Choose a persona or offer');
      return;
    }
    generate.mutate(icpId ? { icpId } : { theme: offer.trim() });
  };

  const hasPersonas = icps.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Generate Landing Page</DialogTitle>
          <DialogDescription>
            A conversion-focused page is generated from a saved persona or a custom offer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Persona</Label>
              <div className="flex items-center gap-3">
                {icpId && (
                  <button
                    type="button"
                    onClick={() => setIcpId(undefined)}
                    disabled={generate.isPending}
                    className="text-xs text-[#290BD9] hover:text-[#2309C5]"
                  >
                    Clear
                  </button>
                )}
                <FieldError message={error} />
              </div>
            </div>
            {hasPersonas ? (
              <RadioGroup
                value={icpId ?? ''}
                onValueChange={(value) => {
                  setIcpId(value);
                  setError(undefined);
                }}
                disabled={generate.isPending}
                className="gap-2"
              >
                {icps.map((icp) => (
                  <label
                    key={icp.id}
                    htmlFor={`persona-${icp.id}`}
                    className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                      icpId === icp.id ? 'border-[#290BD9] bg-[#EDE9FE]' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <RadioGroupItem value={icp.id} id={`persona-${icp.id}`} className="mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">{icp.name}</div>
                      {icp.title && <div className="text-sm text-gray-500 truncate">{icp.title}</div>}
                    </div>
                  </label>
                ))}
              </RadioGroup>
            ) : (
              <p className="text-sm text-gray-500">No saved personas yet. Describe an offer below.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Offer</Label>
            <Textarea
              value={offer}
              error={!!error}
              rows={3}
              maxLength={2000}
              placeholder="e.g. 20% off annual plans for new B2B customers"
              onChange={(e) => {
                setOffer(e.target.value);
                setError(undefined);
              }}
              disabled={!!icpId || generate.isPending}
            />
            {icpId && (
              <p className="text-xs text-gray-500">
                The selected persona is used. Clear it to describe an offer instead.
              </p>
            )}
          </div>

          <Button
            size="lg"
            className="w-full py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white"
            onClick={handleGenerate}
            disabled={generate.isPending}
          >
            {generate.isPending ? (
              <>
                <Spinner size="sm" className="mr-2" />
                STARTING...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                GENERATE
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
